import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import SearchSelect, { SelectDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { AdminTypes } from '../../../components/Menu';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../shared/Helpers/hooks';
import { Client, Provider } from '../../../shared/Assets/sessionDetailsSVGs';
import { PERMISSIONS } from '../../../App/constants';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	setClient,
	setSearchClient,
	setSearchTherapist,
	setTherapist,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { getClinicalSupervisorsTherapists, getUsers } from '../../../redux/State/userSlice';
import { getClients } from '../../../redux/State/clientSlice/clientSlice';
import { getTherapists, getUserClients } from '../../../redux/State/clientSlice/userClientSlice';

const AdminTools = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const USER = useCheckPermission(PERMISSIONS.USER.READ);
	const CLIENT = useCheckPermission(PERMISSIONS.CLIENT.READ);

	// const loadingClientList = useSelector((state) => state.client.loading);
	// const loadingUserList = useSelector((state) => state.user.loading);
	// const clientsData = useSelector((state) => state.client.clients);
	// const usersData = useSelector((state) => state.user.users);
	const therapists = useSelector((state) => state.userClient.therapists);
	const clinicalSupervisorsTherapists = useSelector((state) => state.clinicalSupervisor.clinicalSupervisorsTherapists);
	const accountId = useSelector((state) => state.account.user.id);
	const role = useSelector((state) => state.account.role);
	const choosenUser = useSelector((state) => state.calendarHelper.filterData.therapist);
	const searchString = useSelector((state) => state.calendarHelper.filterData.searchTherapist);
	const choosenClient = useSelector((state) => state.calendarHelper.filterData.client);
	const searchStringClient = useSelector((state) => state.calendarHelper.filterData.searchClient);
	const choosenUserId = choosenUser?.id;
	const choosenClientId = choosenClient?.id;

	const [users, setUsers] = useState<SelectDataProperties[]>([]);
	const [clients, setClients] = useState<SelectDataProperties[]>([]);
	const [pageSize, setPageSize] = useState<number>(10);
	const [pageSizeClient, setPageSizeClient] = useState<number>(10);
	const [loadMoreClient, setLoadMoreClient] = useState<boolean>(false);
	const [loadMoreUser, setLoadMoreUser] = useState<boolean>(false);

	useEffect(() => {
		if (!USER.permissionGranted) return;
		const clientId = choosenClientId;

		if (!searchString) dispatch(setTherapist(null));

		const timeout = setTimeout(() => {
			if (!clientId) {
				dispatch(getUsers({ page: '1', pageSize: `${pageSize}`, searchString }))
					.then(unwrapResult)
					.then((response) => {
						const { query, hasNextPage } = response;
						if (!query) return;
						const userData: SelectDataProperties[] = query.map((x) => ({
							id: x.id,
							label: x.fullName,
						}));
						setUsers(userData);
						setLoadMoreUser(Boolean(hasNextPage));
					})
					.catch(errorNormalizer);

				return;
			}
			dispatch(getTherapists({ clientId }));
		}, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [pageSize, searchString, choosenClientId, dispatch, USER.permissionGranted]);

	useEffect(() => {
		if (!CLIENT.permissionGranted) return;
		if (!searchStringClient) dispatch(setClient(null));
		const userId = choosenUserId;

		if (userId) {
			const timeout = setTimeout(() => {
				dispatch(
					getUserClients({
						userId,
						page: '1',
						pageSize: `${pageSizeClient}`,
						searchString: searchStringClient,
						insuranceIds: [],
					}),
				)
					.then(unwrapResult)
					.then((response) => {
						const { query, hasNextPage } = response;
						if (!query) return;
						const data: SelectDataProperties[] = query.map((x) => ({
							id: x.id,
							label: x.fullName,
						}));
						setClients(data);
						setLoadMoreClient(Boolean(hasNextPage));
					})
					.catch(errorNormalizer);
			}, 1000);

			// TODO - return void as in return () => void clearTimeout(timeout);
			// eslint-disable-next-line
			return () => clearTimeout(timeout);
		}

		const timeout = setTimeout(() => {
			dispatch(
				getClients({
					page: '1',
					pageSize: `${pageSizeClient}`,
					searchString: searchStringClient,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					const data: SelectDataProperties[] = query.map((x) => ({
						id: x.id,
						label: x.fullName,
					}));
					setClients(data);
					setLoadMoreClient(Boolean(hasNextPage));
				})
				.catch(errorNormalizer);
		}, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [searchStringClient, dispatch, pageSizeClient, choosenUserId, CLIENT.permissionGranted]);

	useEffect(() => {
		if (!therapists?.length) return;

		if (!searchString.length) {
			const list = therapists.map((x) => ({ id: x.id, label: x.fullName }));
			setUsers(list);
			setLoadMoreUser(false);

			return;
		}
		const list = therapists
			.filter((x) => x.fullName.toLowerCase().includes(searchString.toLowerCase()))
			.map((x) => ({ id: x.id, label: x.fullName }));
		setUsers(list);
	}, [therapists, searchString]);

	useEffect(() => {
		if (!clinicalSupervisorsTherapists?.length) return;

		if (!searchString.length) {
			const list = clinicalSupervisorsTherapists.map((x) => ({
				id: x.id,
				label: x.fullName,
			}));
			setUsers(list);
			setLoadMoreUser(false);

			return;
		}
		const list = clinicalSupervisorsTherapists
			.filter((x) => x.fullName.toLowerCase().includes(searchString.toLowerCase()))
			.map((x) => ({ id: x.id, label: x.fullName }));
		setUsers(list);
	}, [clinicalSupervisorsTherapists, searchString]);

	useEffect(() => {
		if (!accountId || role?.section.id !== AdminTypes.cs) return;
		dispatch(getClinicalSupervisorsTherapists(accountId));
	}, [accountId, role, dispatch]);

	const therapistHandler = (value: string) => {
		const user = users.find((x) => x.id === value);
		dispatch(setTherapist(user));

		// if (role?.section.id === AdminTypes.cs) {
		//   const therapist = clinicalSupervisorsTherapists?.find((x) => x.id === value);
		//   if (!!therapist) dispatch(setTherapist(therapist));
		//   return;
		// }
		// const user = usersData.query?.find((x) => x.id === value);
		// if (!!user) dispatch(setTherapist(user));
		// const therapist = therapists?.find((x) => x.id === value);
		// if (!!therapist) dispatch(setTherapist(therapist));
	};

	const clientHandler = (value: string) => {
		const client = clients.find((x) => x.id === value);
		dispatch(setClient(client));
	};

	const searchProviderHandler = (value: string) => {
		dispatch(setSearchTherapist(value));
	};

	const searchClientHandler = (value: string) => {
		dispatch(setSearchClient(value));
	};

	return (
		<>
			<SearchSelect
				data={users}
				label="Find Provider:"
				setSearchString={searchProviderHandler}
				searchString={searchString}
				setValue={therapistHandler}
				hasMore={loadMoreUser}
				setSize={setPageSize}
				startAdornment={<Provider />}
			/>
			{CLIENT.permissionGranted && (
				<SearchSelect
					data={clients}
					label="Find Client:"
					setSearchString={searchClientHandler}
					searchString={searchStringClient}
					setValue={clientHandler}
					hasMore={loadMoreClient}
					setSize={setPageSizeClient}
					startAdornment={<Client />}
				/>
			)}
		</>
	);
};

export default AdminTools;
